import type { WinnerImportSupply, WinnerImportSupplyCatalog, WinnerImportSupplyDecision } from './winner-import.api'

/**
 * What the user answered for one Winner catalogue (SOLY-IMPORT-003 S12): buy from one of the
 * organisation's suppliers (null: « fournisseur à préciser »), buy from a supplier the import creates
 * under a name, make it, or nothing to buy.
 */
export type SupplyChoice =
  | { mode: 'buy'; supplierId: string | null }
  | { mode: 'create'; name: string }
  | { mode: 'make' }
  | { mode: 'none' }

export const TO_SAY_VALUE = '__to_say__'
export const CREATE_SUPPLIER_VALUE = '__create__'

/** The review starts from the server's proposal for each catalogue: never a creation. */
export function initialSupplyChoices(supply: WinnerImportSupply | null | undefined): Record<string, SupplyChoice> {
  const choices: Record<string, SupplyChoice> = {}
  for (const catalog of supply?.catalogs ?? []) choices[catalog.key] = proposalOf(catalog)
  return choices
}

function proposalOf(catalog: WinnerImportSupplyCatalog): SupplyChoice {
  if (catalog.mode === 'make') return { mode: 'make' }
  if (catalog.mode === 'none') return { mode: 'none' }
  return { mode: 'buy', supplierId: catalog.supplierId }
}

/** The supplier Select's value for a « buy » or « create » choice. */
export function supplierValueOf(choice: SupplyChoice): string {
  if (choice.mode === 'create') return CREATE_SUPPLIER_VALUE
  if (choice.mode === 'buy') return choice.supplierId ?? TO_SAY_VALUE
  return TO_SAY_VALUE
}

/** The choice a supplier Select value means; « create » keeps the name typed so far, else the proposed one. */
export function supplierChoiceOf(value: string, catalog: WinnerImportSupplyCatalog, previous: SupplyChoice): SupplyChoice {
  if (value === CREATE_SUPPLIER_VALUE)
    return { mode: 'create', name: previous.mode === 'create' ? previous.name : catalog.newSupplierName ?? '' }
  return { mode: 'buy', supplierId: value === TO_SAY_VALUE ? null : value }
}

/** A « create » answer needs a name; the others are always complete (Acheter without supplier included). */
export function isSupplyChoiceComplete(choice: SupplyChoice): boolean {
  return choice.mode !== 'create' || choice.name.trim().length > 0
}

/**
 * What the commit sends: every catalogue as the user left it, saying which ones the review showed
 * answered. An answered catalogue left as it was is not sent: the commit then applies the organisation's
 * answer as it stands, rather than writing back one changed since the preview (Codex review).
 */
export function supplyDecisions(
  choices: Record<string, SupplyChoice>,
  supply?: WinnerImportSupply | null,
): WinnerImportSupplyDecision[] {
  const catalogs = new Map((supply?.catalogs ?? []).map((c) => [c.key, c]))
  const mapped = new Set((supply?.catalogs ?? []).filter((c) => c.kind === 'mapped').map((c) => c.key))
  const unchanged = (key: string, choice: SupplyChoice) => {
    const catalog = catalogs.get(key)
    if (!catalog || catalog.kind !== 'mapped') return false
    if (choice.mode === 'buy') return catalog.mode === 'buy' && (catalog.supplierId ?? null) === choice.supplierId
    return choice.mode === catalog.mode
  }
  return Object.entries(choices).filter(([key, choice]) => !unchanged(key, choice)).map(([key, choice]): WinnerImportSupplyDecision => {
    const wasMapped = mapped.has(key)
    if (choice.mode === 'create') return { key, mode: 'buy', createSupplierName: choice.name.trim(), wasMapped }
    if (choice.mode === 'buy') return { key, mode: 'buy', supplierId: choice.supplierId, wasMapped }
    return { key, mode: choice.mode, wasMapped }
  })
}

/** « 6 catalogues · 1 fournisseur créé · 2 fournisseurs à préciser », the block's folded line. */
export function supplySummary(choices: Record<string, SupplyChoice>, catalogCount: number): string {
  const values = Object.values(choices)
  const created = new Set(values.flatMap((c) => (c.mode === 'create' ? [c.name.trim().toLowerCase()] : []))).size
  const toSay = values.filter((c) => c.mode === 'buy' && !c.supplierId).length
  const parts = [`${catalogCount} catalogue${catalogCount > 1 ? 's' : ''}`]
  if (created > 0) parts.push(`${created} fournisseur${created > 1 ? 's' : ''} créé${created > 1 ? 's' : ''}`)
  if (toSay > 0) parts.push(`${toSay} fournisseur${toSay > 1 ? 's' : ''} à préciser`)
  return parts.join(' · ')
}
